<template>
    <div id="calc" class="calc-container" v-if="!isLoading">
        <h1 class="title-page is-size-5 is-size-4-desktop has-text-weight-bold">
            Compound Returns Calculator
        </h1>
        <div id="explanation">
            <h3>Fill in 3 of the 4 boxes to see your results, hit Reset to clear.</h3>
        </div>
        <div class="inputs-container" :class="showListOptionCalculator ? 'show-border' : ''">
            <ul id="inputs" class="unordered-list" style="min-width: fit-content;">
                <li>I'll start investing with
                    <CustomCurrencyInput placeholder="£…" id="capitalToInvest" v-model="capitalToInvest"
                        :required="true" :disabled="inputs_disabled" :onBlur="calc" :size="12"></CustomCurrencyInput>
                </li>
                <li>I'm aiming to get to
                    <CustomCurrencyInput placeholder="£…" id="aimingToGet" v-model="aimingToGet" 
                    :required="true" :disabled="inputs_disabled" :onBlur="calc" :size="12"></CustomCurrencyInput>
                </li>
                <li> after
                    <input class="has-text-centered" type="text" :disabled="inputs_disabled" v-on:blur="calc" size="2" min="1" required
                        v-model="years" @keypress="isNumber($event)" placeholder="YY">
                    years
                </li>
                <li>
                    My annualised NTR <button v-tooltip="{content: 'Net Total Return', trigger: 'hover click'}">?</button> shall exceed
                    <CustomPercentageInput placeholder="...%" id="annualisedNetTotalReturn" v-model="annualisedNetTotalReturn"
                    :required="true" :disabled="inputs_disabled" :onBlur="calc" :size="5"></CustomPercentageInput>
                </li>
                <button v-on:click="resetValues" class="btn-reset">Reset</button>
            </ul>
            <div class="list-option-calculator" v-if="showListOptionCalculator" style="min-width: 400px;">
                <span class="label-for-adjust">
                    Select the parameter you want to adjust <br/> and the value you want to see <br/> recalculated
                </span>
                <span class="label-for-increment">
                    Define the increment <br/>
                    values <br/>
                    you want to adjust by
                </span>
                <ul id="adjust-unordered-list" class="unordered-list-centered" v-if="visibleAdjust">
                    <li class="radio-button">Adjust</li>
                    <li class="adjust-item" :class="{ active: inputAdjustCapitalToInvestVisible }">
                        <input type="radio" id="r1" :disabled="radioAdjustCapitalToInvestDisabled" name="rGroupAdjust"
                            v-model="selectedRadioAdjust" value="capitalToInvest">
                        <input type="number" class="input-adjust" id="inputAdjustCapitalToInvest"
                            v-if="inputAdjustCapitalToInvestVisible" size=1 step=1000 v-model="capitalToInvest">
                    </li>
                    <li class="between-button adjust-item" :class="{ active: inputAdjustAimingToGetVisible }">
                        <input type="radio" id="r2" :disabled="radioAdjustAimingToGetDisabled" name="rGroupAdjust"
                                v-model="selectedRadioAdjust" value="aimingToGet">
                            <input type="number" class="input-adjust" id="inputAdjustAimingToGet"
                                v-if="inputAdjustAimingToGetVisible" size=1 step=1000 v-model="aimingToGet">
                    </li>
                    <li class="between-button adjust-item" :class="{ active: inputAdjustYearsVisible }">
                        <input type="radio" id="r3" :disabled="radioAdjustYearsDisabled" name="rGroupAdjust"
                            v-model="selectedRadioAdjust" value="years">
                        <input type="number" class="input-adjust" id="inputAdjustYears"
                            v-if="inputAdjustYearsVisible" step=1 v-model="years">
                    </li>
                    <li class="between-button adjust-item" :class="{ active: inputAdjustAnnualisedNetTotalReturnVisible }">
                        <input type="radio" id="r4" :disabled="radioAdjustAnnualisedNetTotalReturnDisabled"
                            name="rGroupAdjust" v-model="selectedRadioAdjust" value="annualisedNetTotalReturn">
                        <input type="number" class="input-adjust" id="inputAdjustAnnualisedNetTotalReturn"
                            v-if="inputAdjustAnnualisedNetTotalReturnVisible" size=1 step=0.5
                            v-model="annualisedNetTotalReturn">
                    </li>
                </ul>
                <ul id="recalculte-unordered-list" class="unordered-list-centered" v-if="visibleRecalculate">
                    <li class="radio-button">Recalculate</li>
                    <li class="between-button"><input type="radio" id="r1"
                            :disabled="radioRecalculatCapitalToInvestDisabled" name="rGroupRecalculate"
                            v-model="selectedRadioRecalculate" value="capitalToInvest"></li>
                    <li class="between-button"><input type="radio" id="r2" :disabled="radioRecalculatAimingToGetDisabled"
                        name="rGroupRecalculate" v-model="selectedRadioRecalculate" value="aimingToGet"></li>
                    <li class="between-button"><input type="radio" id="r3" :disabled="radioRecalculatYearsDisabled"
                            name="radioGroupRecalculate" v-model="selectedRadioRecalculate" value="years"></li>
                    <li class="between-button"><input type="radio" id="r4"
                            :disabled="radioRecalculatAnnualisedNetTotalReturnDisabled" name="radioGroupRecalculate"
                            v-model="selectedRadioRecalculate" value="annualisedNetTotalReturn"></li>
                </ul>
                <ul id="increment-unordered-list" class="unordered-list-centered" v-if="visibleIncrement">
                    <li class="radio-button">Increment</li>
                    <li><input type="text" size=7 v-model="capitalToInvestInc" @keypress="isNumber($event)"></li>
                    <li><input type="text" size=7 v-model="aimingToGetInc" @keypress="isNumber($event)"></li>
                    <li><input type="text" size=7 v-model="yearsInc" @keypress="isNumber($event)"></li>
                    <li><input type="text" size=7 v-model="annualisedNetTotalReturnInc" @keypress="isNumber($event)"></li>
                </ul>
            </div>
        </div>
        <div id="outputs" v-if="visibleOutputs">
            <p v-if="visibleOutputsSecondSentence">With an annual inflation rate of
                <CustomPercentageInput placeholder="...%" id="annualInflationRate" v-model="annualInflationRate"
                    :required="true" :onBlur="calc" :size="3"></CustomPercentageInput>
                , {{ formatGBP(outAimingToGet) }}
                in {{ parseFloat(outYears).toFixed(1) }} years will be equivalent to
                {{ formatGBP(finalAmountAdjustedForInflation) }} in today's money.
            </p>

            <p v-if="visibleOutputsThirdSentence">Only the below fund categories have exceeded the required
                annualised net total return ({{ parseFloat(outAnnualisedNetTotalReturn).toFixed(1) }}%)
                <span class="font-weight-700 red">over {{ getNumYear(years, true) }}</span></p>
        </div>
      <div ref="fundCategoryTable">
        <table class="fund-category-table" v-if="fundCategories.length">
            <tr>
                <th class="fund-category-column">Fund category</th>
                <th width="28%">Median NTR {{ getNumYear(years) }}</th>
                <th width="25%">No. of funds</th>
            </tr>
            <tr v-for="(fundCate, index) in fundCategories" :key="index" @click="viewFundCategoryDetail(fundCate)">
                <td class="fund-category-column">{{ fundCate.categoryname }}</td>
                <td>{{ Number(fundCate.medianNTR1Y || fundCate.medianNTR3Y || fundCate.medianNTR5Y).toFixed(1) }}%</td>
                <td>{{ fundCate.funds }}</td>
            </tr>
        </table>
      </div>
    </div>
    <div class="preloader-center" v-else>
        <Preloader />
    </div>
</template>
<script>
import axios from "axios";
import commonMixin from "@/mixins/commonMixin";
import Preloader from "@/components/Preloader";
import CustomCurrencyInput from "./CustomCurrencyInput.vue";
import CustomPercentageInput from "./CustomPercentageInput.vue";

export default {
    name: 'Calculator',
    mixins: [commonMixin],
    components: { Preloader, CustomCurrencyInput, CustomPercentageInput },
    data() {
        return {
            capitalToInvest: '',
            aimingToGet: '',
            years: '',
            annualisedNetTotalReturn: '',
            outCapitalToInvest: 0,
            outAimingToGet: 0,
            outYears: 0,
            outAnnualisedNetTotalReturn: 0,
            riskFreeFinalAmount: 0,
            annualisedRiskFreeRate: 0,
            annualInflationRate: 2,
            finalAmountAdjustedForInflation: 0,
            inputs_disabled: false,
            visibleOutputs: false,
            visibleOutputsFirstSentence: false,
            visibleOutputsSecondSentence: false,
            visibleOutputsThirdSentence: false,
            selectedRadioAdjust: 'years',
            selectedRadioRecalculate: 'annualisedNetTotalReturn',
            radioAdjustCapitalToInvestDisabled: false,
            radioAdjustAimingToGetDisabled: false,
            radioAdjustYearsDisabled: false,
            radioAdjustAnnualisedNetTotalReturnDisabled: false,
            radioRecalculatCapitalToInvestDisabled: false,
            radioRecalculatAimingToGetDisabled: false,
            radioRecalculatYearsDisabled: false,
            radioRecalculatAnnualisedNetTotalReturnDisabled: false,
            inputAdjustCapitalToInvestVisible: false,
            inputAdjustAimingToGetVisible: false,
            inputAdjustYearsVisible: true,
            inputAdjustAnnualisedNetTotalReturnVisible: false,
            capitalToInvestInc: 1000,
            aimingToGetInc: 1000,
            yearsInc: 1,
            annualisedNetTotalReturnInc: 0.5,
            fundCategories: [],
            isLoading: false,
            visibleAdjust: false,
            visibleRecalculate: false,
            visibleIncrement: false,
        }
    },
    computed: {
        showListOptionCalculator() {
            return this.visibleAdjust || this.visibleRecalculate || this.visibleIncrement
        }
    },
    methods: {
        formatGBP(value) {
            let price = parseFloat(value).toFixed(2);
            const indexDots = price.indexOf('.');
            const integerPart = price.slice(0, indexDots);
            const integerPartFormated = new Intl.NumberFormat('en-US').format(integerPart);
            let decimalPart = price.slice(indexDots, price.length);
            if (decimalPart === '.00') {
                decimalPart = '';
            } else if (decimalPart[decimalPart.length - 1] == 0) {
                decimalPart = decimalPart.slice(0, -1);
            }
            return `£${integerPartFormated}${decimalPart ? decimalPart : ''}`
        },
        async search(obj){
            this.$store.commit("search/SET_PLATFORM_FILTER", null)
            this.$store.commit('search/SET_LATEST_SPREAD_BENCHMARK_DATA', null)
            this.$store.commit('search/SET_COMPARE_BENCHMARK_FILTER', null)
            this.$refs.fundCategoryTable.classList.add('pointer-none')
            await this.$store.dispatch("search/apiCall", obj)
            this.$store.commit("search/SET_ABSOLUTETOTAL", this.$store.state.search.searchData.total)
            const textURL = this.convertURL(obj.text.replaceAll('/', '*'))
            this.$store.commit("search/SET_NEW_SEARCH_SECTION", true)
            const routeData = await this.$router.resolve({name:"Results", params: {
                    id: this.$store.state.search.searchData.searchdata_id,
                    selectedWatchlistID: this.selectedWatchlistID,
                    text: textURL!==''?textURL:'All'}})
            this.$refs.fundCategoryTable.classList.remove('pointer-none')
            window.open(routeData.href, '_blank');
        },
        viewFundCategoryDetail(fundCate) {
            const searchObj ={
                fund: fundCate.categoryname,
                type: 'category',
                text: fundCate.categoryname,
                code: [fundCate.categoryId],
                source: 2,
            }
            this.search(searchObj);
        },
        getFundCategories(numYear, ntr) {
            const urlGetFundCategories = `/fund-category/${numYear}/${ntr}`
            axios.get(urlGetFundCategories).then(response => {
                if (response) {
                    this.fundCategories = response.data;
                }
            })
        },
        isNumber(evt) {
            const keysAllowed = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.']
            const keyPressed = evt.key
            if (!keysAllowed.includes(keyPressed)) {
                evt.preventDefault()
            }
        },
        resetValues() {
            this.fundCategories = []
            this.capitalToInvest = ''
            this.aimingToGet = ''
            this.years = ''
            this.annualisedNetTotalReturn = ''
            this.riskFreeFinalAmount = 0
            this.annualisedRiskFreeRate = 0
            this.annualInflationRate = 2
            this.finalAmountAdjustedForInflation = 0
            this.inputs_disabled = false
            this.visibleOutputs = false
            this.visibleOutputsFirstSentence = false
            this.visibleOutputsSecondSentence = false
            this.visibleOutputsThirdSentence = false,
                this.selectedRadioAdjust = 'years',
                this.selectedRadioRecalculate = 'annualisedNetTotalReturn'
            this.capitalToInvestInc = 1000,
                this.aimingToGetInc = 1000,
                this.yearsInc = 1,
                this.annualisedNetTotalReturnInc = 0.5
            this.visibleAdjust = false
            this.visibleRecalculate = false
            this.visibleIncrement = false
        },
        getAnnualisedRiskFreeRate(x0, y0, x1, y1, years) {
            return ((y0 * (x1 - years) + y1 * (years - x0)) / (x1 - x0))
        },
        getRiskFreeFinalAmount(annualisedRiskFreeRate, years, capitalToInvest) {
            return (((1 + (annualisedRiskFreeRate / 100)) ** years) * capitalToInvest)
        },
        getFinalAmountAdjustedForInflation() {
            return (((1 + (Number(this.annualisedNetTotalReturn) / 100 - this.annualInflationRate / 100)) ** Number(this.years)) * Number(this.capitalToInvest)).toFixed(0)
        },
        calculate() {
            const xs = [0, 1, 2, 3, 5, 7, 10, 20, 30, Number.MAX_SAFE_INTEGER] // years
            const ys = [5.0011, 5.0011, 5.0011, 4.7635, 4.388, 4.207, 4.337, 4.743, 4.783, 4.783] // rate
            let i = 0;
            while (xs[i] < this.years) i++;
            let x0 = xs[i - 1]
            let y0 = ys[i - 1]
            let x1 = xs[i]
            let y1 = ys[i]
            this.annualisedRiskFreeRate = this.getAnnualisedRiskFreeRate(x0, y0, x1, y1, this.years).toFixed(4)
            this.riskFreeFinalAmount = this.getRiskFreeFinalAmount(this.annualisedRiskFreeRate, this.years, this.capitalToInvest).toFixed(0)
        },
        doTasks() {
            this.fill_outputs()
            this.calculate()
            this.finalAmountAdjustedForInflation = this.getFinalAmountAdjustedForInflation()
            if (!this.inputs_disabled) {
                axios.post('/log-data-calculation', {
                    "initialamt" : this.capitalToInvest,
                    "finalamt" : this.aimingToGet,
                    "numyears" : this.years,
                    "ntr" : this.annualisedNetTotalReturn,
                    "user_id" : this.$store.state.auth.userProfile ? this.$store.state.auth.userProfile.id || '' : '',
                })
            }
            this.inputs_disabled = true
            this.setVisibleAdjustRecalculateIncrement()
        },
        calc() {
            const DECIMAL_NUMBERS = 2
            if (!this.inputs_disabled) {
                if (this.capitalToInvest != '' && this.annualisedNetTotalReturn != '' && this.years != '') {
                    this.aimingToGet = (this.capitalToInvest * ((1 + this.annualisedNetTotalReturn / 100) ** this.years)).toFixed(DECIMAL_NUMBERS)
                    this.doTasks()
                }
                else if (this.capitalToInvest != '' && this.annualisedNetTotalReturn != '' && this.aimingToGet != '') {
                    this.years = (Math.log(this.aimingToGet / this.capitalToInvest) / Math.log(1 + (this.annualisedNetTotalReturn / 100))).toFixed(DECIMAL_NUMBERS)
                    this.doTasks()
                }
                else if (this.capitalToInvest != '' && this.years != '' && this.aimingToGet != '') {
                    this.annualisedNetTotalReturn = (((this.aimingToGet / this.capitalToInvest) ** (1 / this.years) - 1) * 100).toFixed(DECIMAL_NUMBERS)
                    this.doTasks()
                    this.inputAdjustYearsDisabled = false
                }
                else if (this.annualisedNetTotalReturn != '' && this.years != '' && this.aimingToGet != '') {
                    this.capitalToInvest = (this.aimingToGet / (1 + (this.annualisedNetTotalReturn / 100)) ** this.years).toFixed(DECIMAL_NUMBERS)
                    this.doTasks()
                }
                this.setStepInputAdjust()
            }
            else {
                if (this.selectedRadioRecalculate == 'capitalToInvest') {
                    this.capitalToInvest = (this.aimingToGet / (1 + (this.annualisedNetTotalReturn / 100)) ** this.years).toFixed(DECIMAL_NUMBERS)
                    this.doTasks()
                }
                if (this.selectedRadioRecalculate == 'aimingToGet') {
                    this.aimingToGet = (this.capitalToInvest * ((1 + this.annualisedNetTotalReturn / 100) ** this.years)).toFixed(DECIMAL_NUMBERS)
                    this.doTasks()
                }
                if (this.selectedRadioRecalculate == 'years') {
                    this.years = (Math.log(this.aimingToGet / this.capitalToInvest) / Math.log(1 + (this.annualisedNetTotalReturn / 100))).toFixed(DECIMAL_NUMBERS)
                    this.doTasks()
                }
                if (this.selectedRadioRecalculate == 'annualisedNetTotalReturn') {
                    this.annualisedNetTotalReturn = (((this.aimingToGet / this.capitalToInvest) ** (1 / this.years) - 1) * 100).toFixed(DECIMAL_NUMBERS)
                    this.doTasks()
                }
            }
            // 1st sentence should appear only when initial amount and num years have been filled in the top box
            if (this.capitalToInvest != '' && this.years != '') {
                this.visibleOutputs = true
                this.visibleOutputsFirstSentence = true
            }
            // 2nd sentence should appear only when final amount and num years have been filled in the top box
            if (this.aimingToGet != '' && this.years != '') {
                this.visibleOutputs = true
                this.visibleOutputsSecondSentence = true
            }
            // 3rd sentence should appear only when rate of return & num years have been filled in the top box  
            if (this.annualisedNetTotalReturn != '' && this.years) {
                this.visibleOutputs = true
                this.visibleOutputsThirdSentence = true
            }
        },
        fill_outputs() {
            this.outCapitalToInvest = this.capitalToInvest
            this.outAimingToGet = this.aimingToGet
            this.outYears = this.years
            this.outAnnualisedNetTotalReturn = this.annualisedNetTotalReturn
        },
        setStepInputAdjust() {
            if (document.getElementById("inputAdjustCapitalToInvest")) {
                document.getElementById("inputAdjustCapitalToInvest").step = this.capitalToInvestInc
            }
            if (document.getElementById("inputAdjustAimingToGet")) {
                document.getElementById("inputAdjustAimingToGet").step = this.aimingToGetInc
            }
            if (document.getElementById("inputAdjustYears")) {
                document.getElementById("inputAdjustYears").step = this.yearsInc
            }
            if (document.getElementById("inputAdjustAnnualisedNetTotalReturn")) {
                document.getElementById("inputAdjustAnnualisedNetTotalReturn").step = this.annualisedNetTotalReturnInc
            }
        },
        setRecalculateDisabled(r1, r2, r3, r4) {
            this.radioRecalculatCapitalToInvestDisabled = r1
            this.radioRecalculatAimingToGetDisabled = r2
            this.radioRecalculatYearsDisabled = r3
            this.radioRecalculatAnnualisedNetTotalReturnDisabled = r4
        },
        setAdjustVisible(i1, i2, i3, i4) {
            this.inputAdjustCapitalToInvestVisible = i1
            this.inputAdjustAimingToGetVisible = i2
            this.inputAdjustYearsVisible = i3
            this.inputAdjustAnnualisedNetTotalReturnVisible = i4
        },
        setVisibleAdjustRecalculateIncrement() {
            this.visibleAdjust = true
            this.visibleRecalculate = true
            this.visibleIncrement = true
        },
        getNumYear(years, isAcronym = false) {
            if (years <= 1) {
                if (isAcronym)
                    return 'the last year';
                else
                    return '1Y';
            } else if (years <= 3) {
                if (isAcronym)
                    return 'the last 3 years';
                else
                    return '3Y';
            } else {
                if (isAcronym)
                    return 'the last 5 years';
                else
                    return '5Y';
            }
        }
    },
    watch: {
        // Inputs
        capitalToInvest() {
            if (this.capitalToInvest <= 0)
                this.capitalToInvest = ''
            if (this.inputs_disabled)
                this.calc()
        },
        aimingToGet() {
            if (this.aimingToGet <= 0)
                this.aimingToGet = ''
            if (this.inputs_disabled)
                this.calc()
        },
        years() {
            if (this.years <= 0)
                this.years = ''
            if (this.inputs_disabled) {
                this.calc()
                if (this.annualisedNetTotalReturn) {
                    this.getFundCategories(this.getNumYear(this.years), this.annualisedNetTotalReturn);
                }
            }

        },
        annualisedNetTotalReturn() {
            if (this.annualisedNetTotalReturn <= 0)
                this.annualisedNetTotalReturn = ''
            if (this.inputs_disabled) {
                this.calc()
                if (this.years) {
                    this.getFundCategories(this.getNumYear(this.years), this.annualisedNetTotalReturn);
                }
            }
        },
        selectedRadioAdjust() {
            if (this.selectedRadioAdjust == 'capitalToInvest') {
                if (this.selectedRadioRecalculate == 'capitalToInvest')
                    this.selectedRadioRecalculate = 'aimingToGet'
                this.setAdjustVisible(true, false, false, false)
                this.setRecalculateDisabled(true, false, false, false)
            }
            if (this.selectedRadioAdjust == 'aimingToGet') {
                if (this.selectedRadioRecalculate == 'aimingToGet')
                    this.selectedRadioRecalculate = 'years'
                this.setAdjustVisible(false, true, false, false)
                this.setRecalculateDisabled(false, true, false, false)
            }
            if (this.selectedRadioAdjust == 'years') {
                if (this.selectedRadioRecalculate == 'years')
                    this.selectedRadioRecalculate = 'annualisedNetTotalReturn'
                this.setAdjustVisible(false, false, true, false)
                this.setRecalculateDisabled(false, false, true, false)
            }
            if (this.selectedRadioAdjust == 'annualisedNetTotalReturn') {

                if (this.selectedRadioRecalculate == 'annualisedNetTotalReturn')
                    this.selectedRadioRecalculate = 'capitalToInvest'

                this.setAdjustVisible(false, false, false, true)
                this.setRecalculateDisabled(false, false, false, true)
            }
        },
        capitalToInvestInc() {
            if (document.getElementById("inputAdjustCapitalToInvest") !== null)
                document.getElementById("inputAdjustCapitalToInvest").step = this.capitalToInvestInc
        },
        aimingToGetInc() {
            if (document.getElementById("inputAdjustAimingToGet") !== null)
                document.getElementById("inputAdjustAimingToGet").step = this.aimingToGetInc
        },
        yearsInc() {
            if (document.getElementById("inputAdjustYears") !== null)
                document.getElementById("inputAdjustYears").step = this.yearsInc
        },
        annualisedNetTotalReturnInc() {
            if (document.getElementById("inputAdjustAnnualisedNetTotalReturn") !== null)
                document.getElementById("inputAdjustAnnualisedNetTotalReturn").step = this.annualisedNetTotalReturnInc
        }
    }
}
</script>
<style scoped lang="scss">
.has-tooltip {
    margin-left: 0;
    margin-right: 2px;
}
input {
    text-align: right;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: inner-spin-button;
        opacity: 1;
    }
    &[type=text] {
        background-color: #fff;
    }
}

#calc {
    margin: auto;
    width: 100%;
}

h3 {
    margin: 0;
}

#explanation {
    display: flex;
    margin-bottom: 10px;
    &>button {
        margin-left: 5px;
        margin-right: 5px;
    }
}

li {
    padding-bottom: 5px;
}

.inputs-container {
    display: flex;
    gap: 10px;
    justify-content: flex-start;
}
.show-border #inputs li,
.show-border .unordered-list-centered li:not(.radio-button) {
    border-bottom: 1px dashed #000;
}
#inputs {
    border: dashed black 1px;
    border-radius: 15px;
    padding: 20px 0;
    width: 56%;
    display: flex;
    flex-direction: column;
    padding-top: 40px;
    li {
        padding: 0 20px;
        min-height: 57px;
        margin-bottom: 20px;
        border-bottom: 1px dashed transparent;
        align-items: center;
        gap: 5px;
        padding-bottom: 10px;
        display: flex;
    }
}

#gilt {
    border: dashed black 1px;
    border-radius: 15px;
    margin-top: 20px;
}

.unordered-list {
    border: dashed black 1px;
    border-radius: 15px;
    list-style-type: none;
    padding: 0px;
    padding-left: 80px;
    width: 10%;
    &-centered {
        text-align: left;
        border: dashed black 1px;
        border-radius: 15px;
        list-style-type: none;
        width: calc(100% / 3);
        text-align: center;
        li:not(.radio-button) {
            min-height: 57px;
            padding-bottom: 10px;
            margin-bottom: 20px;
        }
    }
}

.radio-button {
    padding-bottom: 0px;
    padding-block: 2px;
    border: none !important;
    line-height: 36px;
}

#outputs {
    border-radius: 15px;
    margin-top: 20px;
    &>p {
        line-height: 30px;
    }
}

.fund-category-table {
    margin-top: 20px;
    background: none;
    border: none;
    border-spacing: 2px;
    box-shadow: none;
    -webkit-box-shadow: none;
    width: 50%;
    & td, & th {
        padding: 5px;
        border: none;
        border-bottom: 1px solid #ddd;
    }
    & tr:hover {
        cursor: pointer;
    }
    & tr th {
        text-align: center;
    }
}

.input-adjust {
    font-size: 34px;
    margin-left: 5px;
    width: 22px;
}

#recalculte-unordered-list {
    text-align: center;
}

#adjust-unordered-list, #recalculte-unordered-list {
    input[type="radio"] {
        width: 18px;
        height: 18px;
    }
}

#adjust-unordered-list {
    li:nth-child(2) {
        &.active {
            div {
                display: flex;
                align-items: flex-start;
            }
        }
    }
    li:nth-child(3) {
        &.active {
            div {
                display: flex;
                align-items: flex-start;
                input:first-child {
                    margin-top: 3px;
                }
            }
        }
    }
}

th.fund-category-column {
    text-align: left !important;
    padding-right: 0.75em;
}

td {
    &.fund-category-column {
        color: #009BDE;
        text-align: left !important;
        text-decoration: underline;
        &:hover {
            color: #363636;
        }
    }
}

.btn-reset {
    align-self: flex-end;
    padding: 0.3em 1em;
    font-size: 18px;
    margin-top: 25px;
    margin-right: 20px;
}

.list-option-calculator {
    border: dashed black 1px;
    display: flex;
    justify-content: space-between;
    border-radius: 15px;
    position: relative;
    width: 44%;
    ul {
        border: none;
    }
    .label-for-adjust, .label-for-increment {
        position: absolute;
        text-align: center;
        font-size: 13px;
        font-weight: 400;
        top: -67px;
    }
    .label-for-adjust {
        width: 282px;
        left: -33px;
    }
    .label-for-increment {
        width: 282px;
        right: -75px;
        align-self: flex-end;
    }
}

.calc-container {
    padding-bottom: 8em;
}

.adjust-item, .between-button {
    display: flex;
    justify-content: center;
    align-items: center;
}
.title-page {
    padding-bottom: 0.75em;
}
.adjust-item.active {
    padding-left: 28px;
}
#increment-unordered-list {
    input {
        width: 80px;
    }
}
@media (max-width: 767px) {
    .list-option-calculator {
        display: none;
    }
    #inputs {
        width: 100%;
    }
}
.pointer-none .fund-category-table,
.pointer-none .fund-category-table * {
    cursor: wait;
}
</style>
